import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PageHero } from "@/components/common/PageHero";
import { Reveal } from "@/components/common/Reveal";
import { CTASection } from "@/components/home/CTASection";
import { useT } from "@/context/LanguageContext";
import { DownloadSimple, X, CheckCircle, FileText } from "@phosphor-icons/react";

const RESOURCE_FALLBACK = "https://images.pexels.com/photos/577210/pexels-photo-577210.jpeg";

function DownloadModal({ resource, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post(`/resources/${resource.id}/download`, form);
      setDone(true);
      toast.success("Your download is ready!");
      if (data.file_url) window.open(data.file_url, "_blank");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-5" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white p-8" onClick={(e) => e.stopPropagation()} data-testid="download-modal">
        <button onClick={onClose} className="float-right text-slate-400 hover:text-ink"><X size={22} /></button>
        {done ? (
          <div className="py-8 text-center">
            <CheckCircle size={48} weight="fill" className="mx-auto text-teal" />
            <h3 className="mt-4 font-heading text-xl font-semibold text-ink">Enjoy your guide!</h3>
            <p className="mt-2 text-sm text-slate-500">We've emailed you a copy too.</p>
          </div>
        ) : (
          <>
            <p className="text-xs uppercase tracking-wider font-semibold text-teal">Free download</p>
            <h3 className="mt-2 font-heading text-xl font-semibold text-ink">{resource.title}</h3>
            <p className="mt-2 text-sm text-slate-500">Enter your details to get instant access.</p>
            <form onSubmit={submit} className="mt-5 space-y-3">
              <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="download-name" className="w-full rounded-md border border-slate-200 px-4 py-3 text-sm outline-none focus:border-teal focus:ring-2 focus:ring-teal/30" />
              <input required type="email" placeholder="Work email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="download-email" className="w-full rounded-md border border-slate-200 px-4 py-3 text-sm outline-none focus:border-teal focus:ring-2 focus:ring-teal/30" />
              <input placeholder="Company (optional)" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="download-company" className="w-full rounded-md border border-slate-200 px-4 py-3 text-sm outline-none focus:border-teal focus:ring-2 focus:ring-teal/30" />
              <button type="submit" disabled={loading} data-testid="download-submit" className="flex w-full items-center justify-center gap-2 rounded-md bg-ink px-6 py-3.5 text-sm font-semibold text-white hover:bg-ink-800 disabled:opacity-60">
                <DownloadSimple size={18} weight="bold" /> {loading ? "Preparing…" : "Download now"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default function Resources() {
  const { lang } = useT();
  const [resources, setResources] = useState([]);
  const [selected, setSelected] = useState(null);
  useEffect(() => { api.get("/resources").then((r) => setResources(r.data)).catch((e) => console.error("Failed to load resources:", e)); }, [lang]);

  return (
    <div data-testid="resources-page">
      <PageHero
        overline="Resources"
        title="Free guides to help you import like a pro"
        subtitle="Download our playbooks, checklists and cheat sheets — built from thousands of real shipments."
        crumbs={[{ label: "Resources" }]}
        image="https://images.pexels.com/photos/577210/pexels-photo-577210.jpeg"
      />
      <section className="bg-white py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {resources.map((r, i) => (
              <Reveal key={r.id} delay={(i % 3) * 0.07}>
                <div data-testid={`resource-card-${i}`} className="group flex h-full flex-col overflow-hidden rounded-xl border border-slate-200 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                  <div className="relative h-44 overflow-hidden">
                    <img src={r.cover_image || RESOURCE_FALLBACK} alt={r.title} onError={(e) => { e.currentTarget.src = RESOURCE_FALLBACK; }} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <span className="absolute left-3 top-3 rounded bg-white/90 px-2.5 py-1 text-[11px] font-semibold text-ink">{r.category}</span>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-heading text-lg font-semibold leading-snug text-ink">{r.title}</h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{r.description}</p>
                    <button onClick={() => setSelected(r)} data-testid={`resource-download-${i}`} className="mt-5 flex items-center justify-center gap-2 rounded-md border border-ink px-5 py-2.5 text-sm font-semibold text-ink transition-colors hover:bg-ink hover:text-white">
                      <DownloadSimple size={16} weight="bold" /> Download free
                    </button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      {selected && <DownloadModal resource={selected} onClose={() => setSelected(null)} />}
      <CTASection />
    </div>
  );
}
